// `useAgentMentionDraft` — @/# mention picker logic for the plain
// textareas of the agent surfaces (Spotlight, ThreadAsk, NoteAsk).
//
// Modeled on `features/channel/hooks/useMentionDraft.ts`, with two
// deliberate differences:
//   * CONTROLLED — the surfaces already own their input state (Spotlight
//     dual-writes `localInput` + the hook `query`; useAgentQA owns
//     `query`), so this hook takes `value`/`onChange` instead of holding
//     the draft itself.
//   * Two triggers — `@` opens the member menu, `#` the entity menu
//     (tasks / notes / group chats), mirroring the BlockNote editors.
//
// Send-time contract: `consumeMentions(finalText)` re-walks the text and
// returns the refs whose `@Label` / `#Label` token appears — picked refs
// (the user may have edited a token away) AND auto-resolved ones: a
// hand-typed token that exactly matches a known candidate's label counts
// as a mention without a dropdown pick, so typing `@design-crew` in full
// is equivalent to picking it. The live highlight uses the same matcher,
// so a token lighting up IS the confirmation it resolved. Partial titles
// never auto-attach — the token must be the whole label, whitespace-
// preceded, and not run into a longer word.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
    displayIdEquals,
    displayIdMatches,
    fullDisplayIdIn,
    pickedByDisplayId,
} from "../../tasks/utils/taskDisplayId";
import { mentionKey, type AgentMentionCandidate, type AgentMentionRef } from "./types";

// Dropdown row cap — long lists are noise; the user narrows by typing.
const MAX_SUGGESTIONS = 8;
// How long typing must pause before an exact task id is looked up.
const LOOKUP_DELAY_MS = 250;

// A task row's display id rides in `subtitle` (see useAgentMentionSources).
const taskDisplayId = (c: AgentMentionCandidate): string | undefined =>
    c.ref.kind === "task" ? c.subtitle : undefined;

interface Trigger {
    char: "@" | "#";
    query: string;
    // Absolute index of the trigger char in `value`.
    start: number;
    // Caret position the query runs up to (insertion end).
    end: number;
}

// How far back from the caret we look for a trigger char. Entity titles
// are capped around this length; anything further back is prose, not a
// mention the user is still narrowing.
const MAX_QUERY_LEN = 60;

/** Resolve the live trigger from the caret position. Active when the
 *  caret sits after a `@`/`#` that starts the input or follows
 *  whitespace (so emails and URL fragments don't open the picker).
 *
 *  Unlike `useMentionDraft.detectTrigger`, the query may span SPACES —
 *  entity titles are usually multi-word ("How to get the most out of
 *  Spotlight"). The menu doesn't stay open forever because the hook
 *  only shows suggestions while the query still substring-matches a
 *  candidate; once the text diverges from every title the picker
 *  closes. Newlines (and another trigger char) still end the scan, and
 *  the lookback is capped at MAX_QUERY_LEN. */
export function detectMentionTrigger(value: string, caret: number): Trigger | null {
    if (caret <= 0 || caret > value.length) return null;
    let i = caret - 1;
    while (i >= 0 && caret - i <= MAX_QUERY_LEN + 1) {
        const ch = value[i];
        if (ch === "@" || ch === "#") {
            if (i === 0 || /\s/.test(value[i - 1] ?? "")) {
                return { char: ch, query: value.slice(i + 1, caret), start: i, end: caret };
            }
            return null;
        }
        if (ch === "\n") return null;
        i -= 1;
    }
    return null;
}

export interface UseAgentMentionDraftArgs {
    value: string;
    onChange: (next: string) => void;
    members: AgentMentionCandidate[];
    entities: AgentMentionCandidate[];
    /** One task by its exact display id, whatever its status (MIP-739):
     *  the entity pool holds open work only. */
    lookupTask?: (displayId: string) => Promise<AgentMentionCandidate | null>;
}

export interface MentionTokenMatch {
    ref: AgentMentionRef;
    start: number;
    end: number;
}

/** The trigger character whose menu serves this ref's kind — MUST match
 *  the pool split in `useAgentMentionSources` (users + groups are the
 *  `@` people menu; every workspace entity is `#`). The token matcher
 *  rebuilds tokens from refs, so a mismatch here makes a picked mention
 *  silently unmatchable (no highlight, dropped from the wire payload).
 */
export const triggerForKind = (kind: AgentMentionRef["kind"]): "@" | "#" =>
    kind === "user" || kind === "group" ? "@" : "#";

/** Locate each picked ref's `@Label` / `#Label` token in `text`.
 *
 *  Shared by `consumeMentions` (send-time pruning) and the in-input
 *  highlight overlay, so what the user SEES highlighted is exactly what
 *  gets sent. Rules: longest label first (a picked `@Alice` can't claim
 *  part of a picked `@AliceB` occurrence), token must start the text or
 *  follow whitespace, must not continue into a longer word, and each
 *  text span is claimed at most once. One match per ref.
 */
export function matchMentionTokens(
    text: string,
    picked: readonly AgentMentionRef[]
): MentionTokenMatch[] {
    if (!text || picked.length === 0) return [];
    // Every token starts with a trigger char; without one in the text
    // nothing can match. Cheap guard — the auto-resolve path feeds the
    // WHOLE candidate pool through here on every keystroke.
    if (!text.includes("@") && !text.includes("#")) return [];
    const refs = [...picked].sort((a, b) => b.label.length - a.label.length);
    const out: MentionTokenMatch[] = [];
    const claimed: Array<[number, number]> = [];
    for (const ref of refs) {
        const token = `${triggerForKind(ref.kind)}${ref.label}`;
        let from = 0;
        while (from <= text.length) {
            const idx = text.indexOf(token, from);
            if (idx === -1) break;
            from = idx + 1;
            // Token must start the text or follow whitespace…
            if (idx > 0 && !/\s/.test(text[idx - 1] ?? "")) continue;
            // …and must not continue into a longer word.
            const endCh = text[idx + token.length];
            if (endCh !== undefined && /\w/.test(endCh)) continue;
            // …and must not overlap a longer, already-claimed token.
            const end = idx + token.length;
            if (claimed.some(([s, e]) => idx < e && end > s)) continue;
            claimed.push([idx, end]);
            out.push({ ref, start: idx, end });
            break;
        }
    }
    return out.sort((a, b) => a.start - b.start);
}

export interface UseAgentMentionDraftReturn {
    /** True when the caller should render the dropdown. */
    pickerOpen: boolean;
    suggestions: AgentMentionCandidate[];
    highlightIndex: number;
    moveHighlight: (delta: number) => void;
    /** Splice the candidate's token into the value at the trigger and
     *  lock the ref. Returns the caret position after the inserted
     *  token so the caller can restore the DOM selection. */
    selectSuggestion: (c: AgentMentionCandidate) => number;
    /** `selectSuggestion` on the highlighted row; null when the picker
     *  is closed / empty. */
    selectHighlighted: () => number | null;
    /** Escape: hide the dropdown for the CURRENT trigger only — it
     *  reopens on the next fresh trigger. */
    closePicker: () => void;
    /** Notify the hook of caret moves (click / arrow keys / typing). */
    setCaret: (pos: number) => void;
    /** Send-time re-validation; see module docstring. Clears picks. */
    consumeMentions: (finalText: string) => AgentMentionRef[];
    /** Character ranges of live mention tokens in `value` — the spans
     *  the highlight overlay marks, and exactly what `consumeMentions`
     *  would send right now. */
    highlightRanges: MentionTokenMatch[];
    /** Clear picked refs + dismissal (e.g. surface closed). */
    reset: () => void;
}

export function useAgentMentionDraft({
    value,
    onChange,
    members,
    entities,
    lookupTask,
}: UseAgentMentionDraftArgs): UseAgentMentionDraftReturn {
    const [caret, setCaretPos] = useState(0);
    // Trigger start the user dismissed with Escape — the picker stays
    // closed while the caret remains inside that same trigger.
    const [dismissedStart, setDismissedStart] = useState<number | null>(null);
    const [highlightIndex, setHighlightIndex] = useState(0);
    // Picked refs live in a ref so `consumeMentions` can read + clear
    // them synchronously inside the submit handler. `picksVersion` is
    // bumped on every mutation purely to invalidate the highlight memo
    // (picks otherwise change in the same handler as a `value` change,
    // but reset()/consume() don't have to).
    const pickedRef = useRef<Map<string, AgentMentionRef>>(new Map());
    const [picksVersion, setPicksVersion] = useState(0);

    const trigger = useMemo(() => detectMentionTrigger(value, caret), [value, caret]);

    // A task found by its exact id that the pool doesn't hold (a closed
    // one), for the `#` query it was looked up for.
    const [lookedUp, setLookedUp] = useState<{
        query: string;
        candidate: AgentMentionCandidate;
    } | null>(null);
    const lookupQuery = trigger?.char === "#" ? trigger.query : "";
    useEffect(() => {
        const id = fullDisplayIdIn(lookupQuery);
        if (!id || !lookupTask || entities.some((c) => displayIdEquals(id, taskDisplayId(c)))) {
            return;
        }
        let live = true;
        const timer = setTimeout(() => {
            void lookupTask(id).then((candidate) => {
                if (live && candidate) setLookedUp({ query: lookupQuery, candidate });
            });
        }, LOOKUP_DELAY_MS);
        return () => {
            live = false;
            clearTimeout(timer);
        };
    }, [lookupQuery, lookupTask, entities]);

    const suggestions = useMemo(() => {
        if (!trigger) return [];
        if (dismissedStart === trigger.start) return [];
        // A bare trigger (or whitespace-only query) shows nothing — the
        // menu opens once the user starts narrowing.
        const q = trigger.query.toLowerCase();
        if (!q.trim()) return [];
        const extra =
            trigger.char === "#" &&
            lookedUp?.query === trigger.query &&
            !entities.some((c) => c.key === lookedUp.candidate.key)
                ? [lookedUp.candidate]
                : [];
        const pool = trigger.char === "@" ? members : [...extra, ...entities];
        // Substring filter + exact→prefix→substring rank, on the RAW
        // query — deliberately not `filterAndRankByText`, which trims.
        // Keeping trailing spaces significant makes multi-word narrowing
        // work ("#fix log" still matches "Fix login bug") while closing
        // the menu right after a pick (the inserted token ends with a
        // space no title contains).
        // A task also matches by its display id ("MIP-739", "mip739");
        // an exact id ranks with an exact title.
        const rank = (c: AgentMentionCandidate): number => {
            const label = c.ref.label.toLowerCase();
            if (label === q || displayIdEquals(q, taskDisplayId(c))) return 0;
            if (label.startsWith(q)) return 1;
            return 2;
        };
        return pool
            .filter(
                (c) =>
                    c.ref.label.toLowerCase().includes(q) || displayIdMatches(q, taskDisplayId(c))
            )
            .sort((a, b) => rank(a) - rank(b))
            .slice(0, MAX_SUGGESTIONS);
    }, [trigger, dismissedStart, members, entities, lookedUp]);

    // Keep the highlight inside the list as it refilters, and reset it
    // whenever the trigger query changes so the top match is primed.
    useEffect(() => {
        setHighlightIndex(0);
    }, [trigger?.start, trigger?.query]);

    // A dismissal only applies to the trigger it happened in; leaving
    // the trigger (or clearing the input) re-arms the picker.
    useEffect(() => {
        if (dismissedStart !== null && trigger?.start !== dismissedStart) {
            setDismissedStart(null);
        }
    }, [trigger?.start, dismissedStart]);

    const setCaret = useCallback((pos: number) => setCaretPos(pos), []);

    const moveHighlight = useCallback(
        (delta: number) => {
            setHighlightIndex((prev) => {
                if (suggestions.length === 0) return 0;
                return Math.min(Math.max(prev + delta, 0), suggestions.length - 1);
            });
        },
        [suggestions.length]
    );

    const selectSuggestion = useCallback(
        (c: AgentMentionCandidate): number => {
            if (!trigger) return caret;
            // A task found by its id goes in as `#MIP-739`, not its title
            // (MIP-739). The label is advisory on the wire — the server
            // resolves the task by id — and the token is matched by label.
            const id = taskDisplayId(c);
            const ref: AgentMentionRef =
                id && pickedByDisplayId(trigger.query, id, c.ref.label)
                    ? { ...c.ref, label: id }
                    : c.ref;
            const inserted = `${trigger.char}${ref.label} `;
            const next = `${value.slice(0, trigger.start)}${inserted}${value.slice(trigger.end)}`;
            const newCaret = trigger.start + inserted.length;
            pickedRef.current.set(c.key, ref);
            setPicksVersion((v) => v + 1);
            onChange(next);
            setCaretPos(newCaret);
            return newCaret;
        },
        [trigger, caret, value, onChange]
    );

    const selectHighlighted = useCallback((): number | null => {
        const c = suggestions[highlightIndex];
        if (!c) return null;
        return selectSuggestion(c);
    }, [suggestions, highlightIndex, selectSuggestion]);

    const closePicker = useCallback(() => {
        if (trigger) setDismissedStart(trigger.start);
    }, [trigger]);

    // The full candidate pool, matched alongside picks so hand-typed
    // exact tokens auto-resolve. Picked refs go FIRST: the matcher's
    // length sort is stable, so for identical labels the picked instance
    // claims the span (same entity → same result; a rare label collision
    // resolves to what the user explicitly chose).
    // A task is also typed by its id, so `#MIP-739` resolves by hand too.
    const candidateRefs = useMemo(
        () => [
            ...[...members, ...entities].map((c) => c.ref),
            ...entities.flatMap((c) => {
                const id = taskDisplayId(c);
                return id ? [{ ...c.ref, label: id }] : [];
            }),
        ],
        [members, entities]
    );

    const consumeMentions = useCallback(
        (finalText: string): AgentMentionRef[] => {
            const picked = Array.from(pickedRef.current.values());
            pickedRef.current = new Map();
            setPicksVersion((v) => v + 1);
            // A ref can match through both its picked and its candidate
            // instance (two occurrences of the same token) — the wire
            // payload carries each entity once.
            const seen = new Set<string>();
            const out: AgentMentionRef[] = [];
            for (const m of matchMentionTokens(finalText, [...picked, ...candidateRefs])) {
                const key = mentionKey(m.ref);
                if (seen.has(key)) continue;
                seen.add(key);
                out.push(m.ref);
            }
            return out;
        },
        [candidateRefs]
    );

    // Live token ranges for the highlight overlay. `picksVersion` keys
    // the pick set; `value` keys the text the tokens are matched in.
    // Duplicate occurrences all highlight (unlike the deduped payload) —
    // visually, every resolved token should read as resolved.
    const highlightRanges = useMemo(
        () =>
            matchMentionTokens(value, [
                ...Array.from(pickedRef.current.values()),
                ...candidateRefs,
            ]),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- picksVersion stands in for pickedRef.current
        [value, picksVersion, candidateRefs]
    );

    const reset = useCallback(() => {
        pickedRef.current = new Map();
        setPicksVersion((v) => v + 1);
        setDismissedStart(null);
        setHighlightIndex(0);
    }, []);

    return {
        pickerOpen: suggestions.length > 0,
        suggestions,
        highlightIndex,
        moveHighlight,
        selectSuggestion,
        selectHighlighted,
        closePicker,
        setCaret,
        consumeMentions,
        highlightRanges,
        reset,
    };
}
